import React, { createContext, useContext, useEffect, useState } from 'react';
import { initializeApp, getApps } from 'firebase/app';
import {
    getAuth,
    Auth,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    User as FirebaseUser,
} from 'firebase/auth';
import { User, UserRole, UserStatus } from '@bst-stellar/shared';

interface SuperAdminAuthContextType {
    user: User | null;
    firebaseUser: FirebaseUser | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
}

const SuperAdminAuthContext = createContext<SuperAdminAuthContextType | undefined>(undefined);

// Firebase config from environment
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Demo credentials
const DEMO_EMAIL = 'root@beyondseek.com';
const DEMO_PASSWORD = 'Aa@12345678';

export function SuperAdminAuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [auth, setAuth] = useState<Auth | null>(null);
    const [isDemoMode, setIsDemoMode] = useState(false);

    // Initialize Firebase
    useEffect(() => {
        // Check if we have Firebase config
        if (!firebaseConfig.apiKey) {
            console.log('SuperAdminAuth: No Firebase config, running in demo mode');
            // Demo mode - but don't auto-login, require explicit sign-in
            setIsDemoMode(true);
            setLoading(false);
            return;
        }

        console.log('SuperAdminAuth: Initializing Firebase');
        // Reuse existing Firebase app or create new one
        const app = getApps().length === 0
            ? initializeApp(firebaseConfig, 'super-admin')
            : getApps()[0];
        const authInstance = getAuth(app);
        setAuth(authInstance);

        // Listen to auth state changes
        const unsubscribe = onAuthStateChanged(authInstance, async (fbUser) => {
            console.log('SuperAdminAuth: onAuthStateChanged, user:', fbUser?.uid);
            setFirebaseUser(fbUser);

            if (fbUser) {
                try {
                    const idToken = await fbUser.getIdToken();
                    // Verify with backend as super admin (no tenant)
                    const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify-super-admin`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ idToken }),
                    });

                    if (response.ok) {
                        const data = await response.json();
                        if (data.user?.role === 'super_admin') {
                            setUser(data.user);
                        } else {
                            console.error('User is not a super admin');
                            setUser(null);
                        }
                    } else {
                        console.error('Super admin auth verify failed');
                        setUser(null);
                    }
                } catch (error) {
                    console.error('Super admin auth error:', error);
                    setUser(null);
                }
            } else {
                setUser(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    async function signIn(email: string, password: string) {
        // Demo mode authentication
        if (isDemoMode) {
            if (email === DEMO_EMAIL && password === DEMO_PASSWORD) {
                // Create mock super admin user on successful demo login
                setUser({
                    id: 'super-admin-1',
                    firebaseUid: 'demo-super-admin-uid',
                    email: DEMO_EMAIL,
                    name: 'Super Admin',
                    role: UserRole.SUPER_ADMIN,
                    tenantId: null as any,
                    status: UserStatus.ACTIVE,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                });
                return;
            } else {
                throw new Error('Invalid demo credentials. Use root@beyondseek.com / Aa@12345678');
            }
        }

        // Real Firebase authentication
        if (!auth) {
            throw new Error('Firebase not initialized');
        }

        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // Manually verify with backend since onAuthStateChanged might not fire
        // if user was already signed in
        const idToken = await userCredential.user.getIdToken();
        const response = await fetch(`${import.meta.env.VITE_API_URL}/auth/verify-super-admin`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken }),
        });

        if (response.ok) {
            const data = await response.json();
            if (data.user?.role === 'super_admin') {
                setUser(data.user);
                setFirebaseUser(userCredential.user);
            } else {
                // Not a super admin - sign out and throw error
                await signOut(auth);
                throw new Error('Access denied. Super admin privileges required.');
            }
        } else {
            // Backend verification failed
            const errorData = await response.json().catch(() => ({}));
            await signOut(auth);
            throw new Error(errorData.message || 'Authentication failed');
        }
    }

    async function logout() {
        if (isDemoMode) {
            setUser(null);
            return;
        }
        if (!auth) throw new Error('Firebase not initialized');
        await signOut(auth);
        setUser(null);
    }

    return (
        <SuperAdminAuthContext.Provider value={{ user, firebaseUser, loading, signIn, logout }}>
            {children}
        </SuperAdminAuthContext.Provider>
    );
}

export function useSuperAdminAuth() {
    const context = useContext(SuperAdminAuthContext);
    if (context === undefined) {
        throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
    }
    return context;
}

